import { useMemo } from 'react';

import {
  activeOptionsStaleTime,
  missingSelected,
  rangeParams,
  TOptionItem,
  TRangeActiveOptions,
  TRangeParams,
} from './active-options';
import { ActiveOptionsListbox } from './ActiveOptionsListbox';

/** Either the catalogue narrowed to the range, or the given ids whatever the range. */
export type TCatalogueRequest = { range: TRangeParams } | { idIn: string[] };

/**
 * Asks a catalogue endpoint for one request. Passed as a prop, so it must be a module-level
 * function: a different one on each render would break the rules of hooks. `R` is the set of
 * requests the catalogue answers.
 */
export type TUseCatalogueOptions<T, R = TCatalogueRequest> = (
  request: R,
  config: { enabled?: boolean; staleTime?: number }
) => { data?: { data: T[] }; isFetching: boolean; isError: boolean };

type CatalogueActiveOptionsProps<T> = {
  activeOptions: TRangeActiveOptions;
  useOptions: TUseCatalogueOptions<T>;
  toItem: (entity: T) => TOptionItem;
  isDark?: boolean;
  selected: string[];
  onChange: (next: string[]) => void;
  onClose?: () => void;
};

/**
 * The options of a filter whose catalogue endpoint narrows by the source's range, plus the
 * selected values that range leaves out, named by id. Mounted only while the dropdown is open,
 * so opening it is what refreshes stale options.
 */
export function CatalogueActiveOptions<T>({
  activeOptions,
  useOptions,
  toItem,
  isDark,
  selected,
  onChange,
  onClose,
}: CatalogueActiveOptionsProps<T>) {
  const { data, isFetching, isError } = useOptions(
    { range: rangeParams(activeOptions) },
    { staleTime: activeOptionsStaleTime(activeOptions.source) }
  );
  const active = useMemo(() => (data?.data ?? []).map(toItem), [data, toItem]);

  const missing = missingSelected(active, selected);
  const { data: resolved } = useOptions(
    { idIn: missing },
    { enabled: !isFetching && missing.length > 0 }
  );
  const resolvedSelected = useMemo(() => (resolved?.data ?? []).map(toItem), [resolved, toItem]);

  return (
    <ActiveOptionsListbox
      isDark={isDark}
      active={active}
      resolvedSelected={resolvedSelected}
      isFetching={isFetching}
      isError={isError}
      selected={selected}
      onChange={onChange}
      onClose={onClose}
    />
  );
}
